import React, { useEffect } from "react";
import { getAuthTokenFromLocalStorage } from "hooks/useAuthToken";
import { useNavigate } from "react-router-dom";
import { useAppSelector, useAppDispatch } from "store";
import NotAuthorized from "pages/Layout/NotAuthorized/NotAuthorized";
import { PATHS } from "./config/paths";
import { getClearPath } from "utils";
import SocketListener from "components/SocketListener/SocketListener";
import Ios4kDownloadPrompt from "components/DownloadButton/components/Ios4kDownloadPrompt";
import useUserDetailRefresh from "hooks/useUserDetailRefresh";
import { showSnackbar } from "store/snackbarSlice";
import { isModuleEnabled, ModuleKey } from "types/permissions";
import { ScreenType } from "components/WarningSnackbar";
import { EventDispatchSnackbar, SnackbarEventDetail } from "router/events";
import { rememberPostLoginPath, takePostLoginPath } from "utils/postLoginRedirect";

const PERMISSION_SCREEN_MAP: Partial<Record<ModuleKey, ScreenType>> = {
  design: ScreenType.TechPack,
  model_training: ScreenType.Train,
  api_usage: ScreenType.ApiAccess,
  // The trend pages' wall speaks about Trend Analysis, not the Design Canvas (REF-7279: a no-plan account reaches
  // Runway Trends / Runway Data from the menu, and the default screen showed the canvas copy).
  trends: ScreenType.TrendAnalysis,
};

// API access is not one of the packages the paywall upsells (which plans grant `api_usage` is a product
// decision, K98), so its popup is the permissions variant — "To access the API" + what API access gives —
// the same reason the API sends for a refused permission (`popup_reason: "permissions"`, HttpClient).
const PERMISSION_REASON_SCREENS: ScreenType[] = [ScreenType.ApiAccess];

interface AuthorizationPageProps {
  children: React.ReactNode;
  requireLogin?: boolean;
  paidPlanNeeded?: boolean;
  /** A list means any of them opens the route (see `RouteConfig.requiredPermission`); the first names the wall. */
  requiredPermission?: ModuleKey | ModuleKey[];
  /** See `RouteConfig.permissionMode`: `"visible"` admits a visible-but-disabled module; the default needs it enabled. */
  permissionMode?: "enabled" | "visible";
  permissionRedirect?: string;
  permissionScreen?: ScreenType;
  /** The route's plain twin (see `RouteConfig.permissionFallback`): without the permission the user lands there, same query, no upsell. */
  permissionFallback?: string;
  routePath: string;
}

const AuthorizationPage = ({
  children,
  requireLogin,
  paidPlanNeeded,
  requiredPermission,
  permissionMode = "enabled",
  permissionRedirect,
  permissionScreen,
  permissionFallback,
  routePath,
}: AuthorizationPageProps) => {
  const hasAuthToken = getAuthTokenFromLocalStorage();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const refreshUserDetail = useUserDetailRefresh();

  const { isAuthorized, isCheckingToken, user } = useAppSelector((state) => state.common);
  const isAuthPage = routePath === PATHS.LOGIN || routePath === PATHS.SIGNUP;
  // The url a signed-out visitor asked for, so the login that follows lands there, not Home (a mailed report link). Read
  // off `window.location` (the app runs under a BrowserRouter, so it is the router's location) when it is needed.
  const requestedPath = () => `${window.location.pathname}${window.location.search}${window.location.hash}`;

  useEffect(() => {
    if (!hasAuthToken && requireLogin && !isCheckingToken) {
      rememberPostLoginPath(requestedPath());
      navigate(getClearPath(PATHS.LOGIN));
    }
  }, [hasAuthToken, isCheckingToken]);

  // Signed in on Login/Signup → away after a second (to prevent flickering). An effect, so the timer dies with the page:
  // scheduled during render it outlived a login that had already navigated to the remembered url, and sent it Home.
  const leaveAuthPage = Boolean(hasAuthToken) && !isCheckingToken && isAuthPage;
  useEffect(() => {
    if (!leaveAuthPage) return;
    const timer = setTimeout(() => navigate(takePostLoginPath() ?? PATHS.HOME), 1000);

    return () => clearTimeout(timer);
  }, [leaveAuthPage]);

  // Handled (401/403/409/503) and rejected responses both leave `user.permissions` as they
  // were and raise `permissionsError` — see `hooks/useUserDetailRefresh`.
  useEffect(() => {
    if (!user.id || !hasAuthToken) return;

    refreshUserDetail(user.id);
  }, [user.id, hasAuthToken]);

  if (isCheckingToken && isAuthPage) {
    return <></>;
  }

  if (leaveAuthPage) return <></>;

  if (!requireLogin) return <>{children}</>;

  if (!hasAuthToken) {
    rememberPostLoginPath(requestedPath());
    navigate("/login");

    return <></>;
  } else if (!isAuthorized && paidPlanNeeded) return isCheckingToken ? <></> : <NotAuthorized />;

  if (requiredPermission) {
    if (!user.permissions || isCheckingToken) return <></>;
    const permissions = user.permissions;
    const required = Array.isArray(requiredPermission) ? requiredPermission : [requiredPermission];
    const primary = required[0];

    // REF-7279: a "visible" route is the workspace, not the paid action — a visible-but-disabled account enters and the
    // paid request's own 402 offers the plan. A hidden module is not for this account at all: home, no upsell.
    if (permissionMode === "visible") {
      if (!required.some((key) => permissions[key]?.is_visible === true)) {
        navigate(permissionRedirect ?? PATHS.HOME, { replace: true });

        return <></>;
      }
    } else if (!required.some((key) => isModuleEnabled(key, permissions, user.packageCode))) {
      // Same predicate the chatbot's guided-offer gate reads (`types/permissions.ts`), so a guided
      // run can never be offered into a route that would bounce the user. Behaviour is unchanged:
      // `!isModuleEnabled(...)` is `isFakeEnterprise || !permission?.is_enabled` — pinned by an
      // exhaustive truth table in `navActions.test.ts`.
      if (permissionFallback) {
        navigate(`${permissionFallback}${window.location.search}`, { replace: true });

        return <></>;
      }
      const screen = permissionScreen ?? PERMISSION_SCREEN_MAP[primary] ?? ScreenType.Design;
      // D2 (STRIPE Phase 3): the module is denied because the plan's payment failed (no grace) — the remedy is fixing the
      // payment, not buying another plan. Otherwise the usual upgrade wall.
      if (user.hasPaymentProblem) {
        // Through the shared snackbar contract (AGENTS.md → Snackbar: `EventDispatchSnackbar` + `messageKey`), the same
        // channel the API's own past_due 402 takes — `errors:subscription_expired` is the key that 402 carries.
        window.dispatchEvent(
          new CustomEvent<SnackbarEventDetail>(EventDispatchSnackbar, {
            detail: { type: 402, screen, messageType: "popup", popupType: "past_due", messageKey: "errors:subscription_expired" },
          })
        );
      } else {
        const popupReason = PERMISSION_REASON_SCREENS.includes(screen) ? "permissions" : undefined;
        dispatch(showSnackbar({ type: 402, screen, messageType: "popup", popupType: "design_studio", ...(popupReason && { popupReason }) }));
      }
      navigate(permissionRedirect ?? PATHS.HOME, { replace: true });

      return <></>;
    }
  }

  return (
    // P1-1: `SocketProvider` moved OUT of here, up to `Router.tsx` above `<Routes>`. This component
    // is instantiated per route, so it took the WS consumer down with it whenever a navigation
    // changed the route's layout — resetting `lastMessage` and the dedup map, and dropping every
    // frame published across that window. `SocketListener` stays: it calls `useNavigate` and must
    // not run on public routes.
    <SocketListener>
      <Ios4kDownloadPrompt />
      {children}
    </SocketListener>
  );
};

export default AuthorizationPage;
